import { Component, OnInit, Input, Inject } from '@angular/core';
import { CampaignDrawerOpen } from '@finder/shared/services/campaign-drawer/campaign-drawer.interface';
import { CommonModule, DOCUMENT } from '@angular/common';
import { filter, takeUntil } from 'rxjs/operators';
import has from 'lodash-es/has';
import get from 'lodash-es/get';

import {
    CampaignDrawerInfographicService
} from '@finder/shared/services/campaign-drawer-infographic/campaign-drawer-infographic.service';
import { CampaignDrawerService } from '@finder/shared/services/campaign-drawer/campaign-drawer.service';
import { CampaignDrawerGroupBase } from '@finder/shared/components/campaign-drawer-base/campaign-drawer-group-base';
import { CreateStyleProvider, CREATE_STYLE } from '@finder/shared/utils/dom/createStyle';
import getCardOrder from './campaign-drawer.util';
import { ComponentHeaderComponent } from '../component-header/component-header.component';
import { CampaignDrawerCardComponent } from '../campaign-drawer-card/campaign-drawer-card.component';
import { CampaignDrawerDetailComponent } from '../campaign-drawer-detail/campaign-drawer-detail.component';
import { GetCardOrderPipe } from './get-card-order.pipe';

@Component({
    selector: 'finder-campaign-drawer-infographic',
    templateUrl: './campaign-drawer-infographic.component.html',
    styleUrls: ['./campaign-drawer-infographic.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ComponentHeaderComponent,
        CampaignDrawerCardComponent,
        CampaignDrawerDetailComponent,
        GetCardOrderPipe,
    ]
})
export class CampaignDrawerInfographicComponent extends CampaignDrawerGroupBase implements OnInit {
    @Input() groupData: any;

    selectedCard = -1;
    css: any;

    constructor(
        private drawerInfographicService: CampaignDrawerInfographicService,
        protected drawerService: CampaignDrawerService,
        @Inject(DOCUMENT) private document: Document,
        @Inject(CREATE_STYLE) private createStyle: CreateStyleProvider,
    ) {
        super(drawerService);
    }

    ngOnInit() {
        this.generateCss();

        // Set the groupId so the caret shows
        this.setGroupId(this.groupData.blockId);

        this.onDataChange
            .pipe(
                filter(data => data !== null),
                takeUntil(this.destroy$)
            )
            .subscribe(data => this.dataChange(data));
    }

    private dataChange(data: CampaignDrawerOpen) {
        // If an order is present set it
        if (has(data, 'cardCssOrder')) {
            this.css = {
                order: data.cardCssOrder
            };
        }
    }

    /* istanbul ignore next */
    private generateCss() {
        if (!this.groupData) {
            return;
        }

        // Create the order for the cards so the detail element can be placed in the correct location
        const cssArray = [];
        const drawerGroups = get(this.groupData, 'drawerGroup', []);
        for (let index = 0; index < drawerGroups.length; index++) {
            const blockNumber = index + 1;
            cssArray.push(`
.blockId-${this.groupData.blockId} .drawer-infographic finder-campaign-drawer-card:nth-child(${blockNumber}) {
    order: ${blockNumber};
}
@media (min-width: 768px) {
    .blockId-${this.groupData.blockId} .drawer-infographic finder-campaign-drawer-card:nth-child(${blockNumber}) {
        order: ${this.getCardOrder(index)};
    }
}`);
        }

        // Add css to the page
        this.createStyle(this.document, cssArray.join(''));
        // Set the css var
        this.getCardPercentage();
    }

    // Generate the order number for desktop cards
    private getCardOrder(index) {
        return getCardOrder(index, this.groupData.count);
    }

    // Get the percentage the card should take up
    /* istanbul ignore next */
    private getCardPercentage() {
        let percent = 0;

        // We want to define the size this uses when the page starts to shrink
        // If there's no row count then make sure they take an equal amount of space
        if (this.groupData.count > this.groupData.drawerGroup.length) {
            percent = Math.floor(100 / this.groupData.drawerGroup.length);
        } else {
            // If there is a count make sure they take up the right amount of space for the row
            percent = Math.floor(100 / this.groupData.count);
        }

        // Set the width to be shared with the cards
        this.drawerInfographicService.setWidth(percent);
    }
}
